import Head from 'next/head';
import Link from 'next/link';

import { Box } from '@zillow/constellation-10';

import { ErrorPage as ErrorPageComponent } from '@zg-rentals/visual-cohesion';
import { ERROR_PAGE_TYPES, ErrorMessageConstants } from '@zg-rentals/zillow-rental-manager/constants';

import {
  EXTERNAL_ROUTES,
  EXTERNAL_URLS,
  getDashboardOverviewRoute,
  getListingRoute,
  ROUTES,
} from '../../constants/urls';

import type { ValueOf } from 'type-fest';

const ERROR_CONFIG = {
  [ERROR_PAGE_TYPES.NOT_FOUND]: {
    title: 'Page not found',
    message: 'We can’t find that page',
    details: 'That page may have moved or no longer exists. Let’s get you back on track.',
    action: <a href={EXTERNAL_ROUTES.ZRM_HOME_PAGE}>Go to Rental Manager</a>,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES]: {
    title: 'Technical difficulties',
    message: 'Something went wrong on our end',
    details: ErrorMessageConstants.TECHNICAL_DIFFICULTIES,
    action: <a href={EXTERNAL_ROUTES.ZRM_HOME_PAGE}>Go to Rental Manager</a>,
    secondaryAction: <a href={EXTERNAL_URLS.SUPPORT_EMAIL}>Contact support</a>,
  },
  [ERROR_PAGE_TYPES.UNAUTHORIZED]: {
    title: 'Unauthorized',
    message: 'Something went wrong',
    details: ErrorMessageConstants.LISTING_NO_PERMISSION,
    action: <a href={EXTERNAL_ROUTES.ZRM_HOME_PAGE}>Go to Rental Manager</a>,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.PREMIUM_ANALYTICS_ERROR]: {
    title: 'Technical difficulties',
    message: 'Something went wrong',
    details: 'We are having trouble loading your data. Try reloading the page or come back later.',
    action: null,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.MULTIPLEX_PROPERTY_LOAD_ERROR]: {
    title: 'Something went wrong',
    message: ErrorMessageConstants.PROPERTY_DETAILS_LOAD_ERROR,
    details: undefined,
    action: <Link href={ROUTES.PROPERTIES}>Go back to properties</Link>,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.STREETEASY_RESTRICTED]: {
    title: 'Listing not available',
    message: 'This property can’t be listed on Zillow',
    details: ErrorMessageConstants.LOCATION_RESTRICTION.NYC,
    action: null,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.PORTFOLIO_ACCESS_DENIED]: {
    title: 'Access denied',
    message: 'You need additional permissions to view this page. Contact your admin to request access if needed.',
    details: undefined,
    action: <Link href="/account">Go to your account</Link>,
    secondaryAction: undefined,
  },
};

type ErrorPageProps = {
  type?: ValueOf<typeof ERROR_PAGE_TYPES>;
  listingAlias?: string;
  errorDetails?: string;
};

const ErrorPage = ({ type = ERROR_PAGE_TYPES.NOT_FOUND, listingAlias, errorDetails }: ErrorPageProps) => {
  const { title, message, details, action, secondaryAction } = ERROR_CONFIG[type];

  const resolvedAction = (() => {
    if (!listingAlias) {
      return action;
    }

    if (type === ERROR_PAGE_TYPES.STREETEASY_RESTRICTED) {
      return <Link href={getDashboardOverviewRoute({ listingAlias })}>Return to your property</Link>;
    }

    if (type === ERROR_PAGE_TYPES.PREMIUM_ANALYTICS_ERROR) {
      return <Link href={getListingRoute({ listingAlias })}>Return to your property</Link>;
    }

    return action;
  })();

  return (
    <>
      <Head>
        <title>{title} - Zillow Rental Manager</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {/*
       * header: 55px MobileHeaderNav (base→xxl), 81px HeaderNavigation (xxl+)
       * footer: Page.Root paddingBlockEnd (64px) + Page.Content marginBlockStart
       *         (24px mobile / 32px tablet+) + TextButton line-height (24px) = 112px / 120px
       */}
      <Box
        css={{
          height: {
            base: 'calc(100dvh - 55px - 112px)',
            md: 'calc(100dvh - 55px - 120px)',
            xxl: 'calc(100dvh - 81px - 120px)',
          },
        }}
      >
        <ErrorPageComponent
          errorMessage={message}
          errorDetails={errorDetails ?? details}
          action={resolvedAction ?? undefined}
          secondaryAction={secondaryAction}
        />
      </Box>
    </>
  );
};

export default ErrorPage;
